/* eslint-disable react-refresh/only-export-components */
import { createContext, isValidElement, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { translateUiText, interpolateMessage } from '../../ui/locale/centralLocalization';
import { useOptionalLanguage } from '../../context/LanguageContext';
import './reception-locale.css';
import { ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';

export type ReceptionLanguage = 'en' | 'ar';
const STORAGE_KEY = 'reception.language';
import { getAccountLanguage } from '../../features/account-settings/preferenceEvents';
import { useStaffLanguageSync } from '../../features/account-settings/useStaffLanguageSync';
const listeners = new Set<() => void>();
let activeLanguage: ReceptionLanguage | null = null;
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const getSnapshot = () => activeLanguage;
const getServerSnapshot = () => null;
function publishLanguage(language: ReceptionLanguage | null) {
  if (language === activeLanguage) return;
  activeLanguage = language;
  listeners.forEach(listener => listener());
}

/** For reception-facing shared overlays rendered above the reception provider. */
export function useActiveReceptionLanguage(): ReceptionLanguage | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Shared engine adapter; preserve the department's authored-copy namespace. */
export function translateReceptionText(text: string, language: ReceptionLanguage): string {
  return translateUiText(text, language, 'reception');
}

/** Render-time helper for UI copy; non-string values retain their identity. */
export function receptionText<T>(value: T): T {
  return (typeof value === 'string' ? translateReceptionText(value, activeLanguage ?? 'en') : value) as T;
}
const metadataKeys = new Set(['label', 'title', 'header', 'hint', 'caption', 'emptyText']);
const dataKeys = new Set(['name', 'value', 'key', 'id', 'course', 'data', 'student', 'customer', 'row', 'lead', 'employee', 'notes', 'content', 'description']);
const configCaches: Record<ReceptionLanguage, WeakMap<object, unknown>> = { en: new WeakMap(), ar: new WeakMap() };
function translateLabels<T>(config: T, language: ReceptionLanguage): T {
  if (!config || typeof config !== 'object' || isValidElement(config)) return config;
  const prototype = Object.getPrototypeOf(config);
  if (!Array.isArray(config) && prototype !== Object.prototype && prototype !== null) return config;
  const cache = configCaches[language];
  if (cache.has(config)) return cache.get(config) as T;
  let result: object = config;
  const preserveDisplayLabel = (config as { translate?: boolean }).translate === false;
  for (const [key, value] of Object.entries(config)) {
    if (dataKeys.has(key) || (preserveDisplayLabel && (key === 'label' || key === 'title'))) continue;
    const next = metadataKeys.has(key) && typeof value === 'string' ? translateReceptionText(value, language) : translateLabels(value, language);
    if (next !== value) {
      if (result === config) result = Array.isArray(config) ? [...config] : Object.assign(Object.create(prototype), config);
      (result as Record<string, unknown>)[key] = next;
    }
  }
  cache.set(config, result);
  return result as T;
}
export function receptionLabels<T>(config: T): T { return translateLabels(config, activeLanguage ?? 'en'); }
export function receptionFormat(template: string, values: readonly unknown[]): string {
  return formatReceptionText(template, values, activeLanguage ?? 'en');
}
function formatReceptionText(template: string, values: readonly unknown[], language: ReceptionLanguage): string {
  return interpolateTemplate(translateReceptionText(template, language), values);
}
function interpolateTemplate(template: string, values: readonly unknown[]): string {
  return interpolateMessage(template, values);
}

interface ReceptionLocale {
  language: ReceptionLanguage;
  setLanguage: (language: ReceptionLanguage) => void;
  toggleLanguage: () => void;
  t: <T>(value: T) => T;
  format: typeof receptionFormat;
  labels: typeof receptionLabels;
}
const englishFallback: ReceptionLocale = { language: 'en', setLanguage: () => undefined, toggleLanguage: () => undefined, t: text => text, format: receptionFormat, labels: receptionLabels };
const ReceptionLocaleContext = createContext<ReceptionLocale | null>(null);
export function useReceptionLocale(): ReceptionLocale {
  const context = useContext(ReceptionLocaleContext);
  const appLanguage = useOptionalLanguage();
  const active = useActiveReceptionLanguage();
  const language = context?.language ?? appLanguage?.language ?? active ?? 'en';
  const scoped = context !== null || appLanguage !== undefined || active !== null;
  const t = useCallback(<T,>(value: T): T => (scoped && typeof value === 'string' ? translateReceptionText(value, language) : value) as T, [language, scoped]);
  const format = useCallback((template: string, values: readonly unknown[]) => scoped ? formatReceptionText(template, values, language) : interpolateTemplate(template, values), [language, scoped]);
  const labels = useCallback(<T,>(config: T): T => scoped ? translateLabels(config, language) : config, [language, scoped]);
  return useMemo(() => context ?? { ...englishFallback, ...(appLanguage ? { setLanguage: appLanguage.setLanguage, toggleLanguage: appLanguage.toggleLanguage } : {}), language, t, format, labels }, [context, appLanguage, language, t, format, labels]);
}

function readLanguage(): ReceptionLanguage {
  const saved=getAccountLanguage(); if(saved)return saved;
  try { return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en'; }
  catch { return 'en'; }
}

const languageClasses = ['rtl', 'ltr', 'lang-ar', 'lang-en'];
const attributes = ['lang', 'dir', 'data-reception-language'];
function captureDocument() {
  const elements = [document.documentElement, document.body];
  const previous = elements.map(element => attributes.map(name => element.getAttribute(name)));
  const previousClasses = languageClasses.map(name => document.body.classList.contains(name));
  return () => {
    elements.forEach((element, index) => attributes.forEach((name, attributeIndex) => {
      const value = previous[index][attributeIndex];
      if (value === null) element.removeAttribute(name); else element.setAttribute(name, value);
    }));
    languageClasses.forEach((name, index) => document.body.classList.toggle(name, previousClasses[index]));
  };
}

function applyDocumentLanguage(language: ReceptionLanguage) {
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  for (const element of [document.documentElement, document.body]) {
    element.lang = language;
    element.dir = direction;
    element.setAttribute('data-reception-language', language);
  }
  languageClasses.forEach(name => document.body.classList.toggle(name, name === direction || name === `lang-${language}`));
}

function StandaloneReceptionLocaleRoot({ children }: { children: ReactNode }) {
  const [language, setLocalLanguage] = useState<ReceptionLanguage>(readLanguage);
  const setLanguage = useStaffLanguageSync(setLocalLanguage);
  return <ReceptionLocaleRoot language={language} setLanguage={setLanguage}>{children}</ReceptionLocaleRoot>;
}
function ReceptionLocaleRoot({ children, language, setLanguage }: {
  children: ReactNode; language: ReceptionLanguage; setLanguage: (language: ReceptionLanguage) => void;
}) {
  const appLanguage = useOptionalLanguage();
  const appScoped = appLanguage !== undefined;
  const languageRef = useRef(language);
  const restoreDocumentRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    languageRef.current = language;
    publishLanguage(language);
    if (appScoped) for (const element of [document.documentElement, document.body]) element.setAttribute('data-reception-language', language);
    if (!appScoped && restoreDocumentRef.current) applyDocumentLanguage(language);
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* Private browsing may disallow storage. */ }
  }, [language, appScoped]);
  useEffect(() => {
    if (appScoped) {
      // The app root exclusively owns lang/dir. Keep scoped styling metadata only.
      for (const element of [document.documentElement, document.body]) element.setAttribute('data-reception-language', languageRef.current);
      return () => {
        for (const element of [document.documentElement, document.body]) element.removeAttribute('data-reception-language');
        publishLanguage(null);
      };
    }
    let mounted = true;
    // Public LanguageProvider runs its initial passive effect after its children.
    // Capture its finished metadata before applying the scoped reception choice.
    queueMicrotask(() => {
      if (!mounted) return;
      restoreDocumentRef.current = captureDocument();
      applyDocumentLanguage(languageRef.current);
    });
    return () => {
      mounted = false;
      restoreDocumentRef.current?.();
      restoreDocumentRef.current = null;
      publishLanguage(null);
    };
  }, [appScoped]);
  const toggleLanguage = useCallback(() => setLanguage(language === 'en' ? 'ar' : 'en'), [language,setLanguage]);
  const t = useCallback(<T,>(value: T): T => (typeof value === 'string' ? translateReceptionText(value, language) : value) as T, [language]);
  const format = useCallback((template: string, values: readonly unknown[]) => formatReceptionText(template, values, language), [language]);
  const labels = useCallback(<T,>(config: T): T => translateLabels(config, language), [language]);
  const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t, format, labels }), [language, setLanguage, toggleLanguage, t, format, labels]);
  return <ReceptionLocaleContext.Provider value={value}><ConfigProvider locale={language === 'ar' ? arEG : enUS}>{children}</ConfigProvider></ReceptionLocaleContext.Provider>;
}

export function ReceptionLocaleProvider({ children }: { children: ReactNode }) {
  const parent = useContext(ReceptionLocaleContext);
  const appLanguage = useOptionalLanguage();
  if (parent) return <>{children}</>;
  return appLanguage
    ? <ReceptionLocaleRoot language={appLanguage.language} setLanguage={appLanguage.setLanguage}>{children}</ReceptionLocaleRoot>
    : <StandaloneReceptionLocaleRoot>{children}</StandaloneReceptionLocaleRoot>;
}
